// src/hooks/useCartStock.js - NEW FILE
import { useState, useEffect } from 'react';
import { getProductDetails } from '../store/api/products';

/**
 * Custom hook to fetch real-time stock data for cart items
 * Only fetches for guest users (authenticated users get fresh data from backend)
 */
export const useCartStock = (cartItems, isAuthenticated) => {
  const [stockData, setStockData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStockData = async () => {
      // Skip if authenticated or no items
      if (isAuthenticated || !cartItems || cartItems.length === 0) {
        setStockData({});
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const stockPromises = cartItems.map(item => 
          getProductDetails(item.productId)
            .then(response => {
              const product = response.data.product;
              
              // console.log(`📦 Fetched product ${item.productId}:`, {
              //   stockLevel: product.stockLevel,
              //   isInStock: product.isInStock,
              //   stockStatus: product.stockStatus,
              //   stock: product.stock
              // });
              
              // ✅ CRITICAL FIX: Convert stockLevel to numeric
              let stock = 0;
              
              if (product.stock || product.stockAvailable || product.quantity) {
                stock = product.stock || product.stockAvailable || product.quantity || 0;
              } else if (product.stockLevel) {
                const stockLevelMap = {
                  'high': 100,
                  'medium': 50,
                  'low': 10,
                  'out_of_stock': 0,
                  'out-of-stock': 0
                };
                stock = stockLevelMap[product.stockLevel.toLowerCase()] || 0;
              } else if (product.isInStock) {
                stock = 999;
              }
              
              const isInStock = product.isInStock !== undefined ? product.isInStock : stock > 0;
              
              // console.log(`✅ Converted stock for ${item.productId}:`, {
              //   originalStockLevel: product.stockLevel,
              //   convertedStock: stock,
              //   finalIsInStock: isInStock
              // });
              
              return {
                productId: item.productId,
                stock: stock,
                stockAvailable: stock,
                isInStock: isInStock,
                stockStatus: product.stockStatus || (stock === 0 ? 'OUT_OF_STOCK' :
                            stock <= 10 ? 'LOW_STOCK' : 'IN_STOCK'),
                stockLevel: product.stockLevel,
                price: product.sellingPrice || product.price,
                mrp: product.mrp
              };
            })
            .catch(error => {
              // console.error(`❌ Failed to fetch stock for ${item.productId}:`, error);
              return {
                productId: item.productId,
                stock: 0,
                stockAvailable: 0,
                isInStock: false,
                stockStatus: 'OUT_OF_STOCK',
                price: item.price,
                mrp: item.mrp
              };
            })
        );

        const stockResults = await Promise.all(stockPromises);
        
        // Create stock lookup map
        const stockMap = {};
        stockResults.forEach(result => {
          stockMap[result.productId] = result;
        });

        setStockData(stockMap);
      } catch (err) {
        // console.error('Error fetching stock data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStockData();
  }, [cartItems, isAuthenticated]);

  /**
   * Get current stock for a specific item
   */
  const getCurrentStock = (item) => {
    if (isAuthenticated) {
      // Authenticated users: use data from backend (already fresh)
      return {
        stock: item.stock || 0,
        stockAvailable: item.stockAvailable || item.stock || 0,
        isInStock: item.isInStock !== undefined ? item.isInStock : (item.stock > 0),
        stockStatus: item.stockStatus || 'IN_STOCK',
        price: item.price,
        mrp: item.mrp
      };
    } else {
      // Guest users: use fetched stock data or fallback to stored data
      return stockData[item.productId] || {
        stock: item.stock || 0,
        stockAvailable: item.stockAvailable || item.stock || 0,
        isInStock: item.isInStock !== undefined ? item.isInStock : false,
        stockStatus: item.stockStatus || 'OUT_OF_STOCK',
        price: item.price,
        mrp: item.mrp
      };
    }
  };

  /**
   * Check if any items are out of stock
   */
  const hasOutOfStockItems = () => {
    if (!cartItems || cartItems.length === 0) return false;
    
    return cartItems.some(item => {
      const currentStock = getCurrentStock(item);
      return !currentStock.isInStock || currentStock.stockAvailable < item.quantity;
    });
  };

  /**
   * Get list of out of stock items
   */
  const getOutOfStockItems = () => {
    if (!cartItems || cartItems.length === 0) return [];
    
    return cartItems.filter(item => {
      const currentStock = getCurrentStock(item);
      return !currentStock.isInStock || currentStock.stockAvailable < item.quantity;
    });
  };

  return {
    stockData,
    loading,
    error,
    getCurrentStock,
    hasOutOfStockItems,
    getOutOfStockItems
  };
};

export default useCartStock;